import { OrniTransportError } from "../transport.ts";
import { decodeAgentIdentity, decodeProviderId } from "../agent-protocol.ts";
import type {
  AutoContinuePolicyDto,
  ProcessAttemptDto,
  ProcessStateDto,
  ProviderFactDto,
  ReceiptOutcomeDto,
  ReceiptStatusDto,
  SessionDto,
  SessionSnapshotDto,
} from "../wire/session-dtos.ts";
import {
  currentDeviceSessionRef,
  type AuthorityRef,
  type AutoContinuePolicy,
  type CommandReceipt,
  type ControllerState,
  type SessionStatus,
  type SessionSummary,
} from "./session.ts";

export function projectSession(
  snapshot: SessionSnapshotDto,
  authority: AuthorityRef,
): SessionSummary {
  const source = snapshot.session;
  const attempt = snapshot.current_attempt;
  const identity = decodeAgentIdentity(
    source.provider_selection,
    "Session.provider_selection.identity",
  );
  const provider = decodeProviderId(
    source.provider_selection.provider_id,
    "Session.provider_selection.provider_id",
  );
  const workspacePath = source.workspace.workspace_id;
  const latestFact = snapshot.provider_facts
    .slice()
    .sort((left, right) => left.observed_at.localeCompare(right.observed_at))
    .at(-1);
  const generation = attempt?.generation ?? Math.max(1, source.next_generation - 1);
  const importedFromProviderHistory = snapshot.provider_facts.some((fact) =>
    fact.attributes.imported === "true"
    || fact.attributes.provider_history_source_id !== undefined
  );
  return {
    ref: { ...currentDeviceSessionRef(source.session_id), authority },
    id: source.session_id,
    shortId: source.session_id.slice(0, 8),
    title: source.title || `${provider} session`,
    provider,
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    managementMode: source.management_mode,
    model: source.provider_selection.integration_mode,
    status: projectStatus(source, attempt),
    providerReadiness: attempt?.provider_readiness ?? "unknown",
    attention: "none",
    visibility: "background",
    workspace: {
      id: workspacePath,
      name: workspaceName(workspacePath),
      path: workspacePath,
      branch: null,
    },
    createdAt: source.created_at,
    updatedAt: source.updated_at,
    lastActivityAt: latestFact && latestFact.observed_at > source.updated_at
      ? latestFact.observed_at
      : source.updated_at,
    unreadCount: 0,
    processAttemptId: attempt?.process_attempt_id ?? null,
    generation,
    activeStageId: null,
    taskProgress: null,
    autoContinue: projectAutoContinue(source.auto_continue),
    controller: unavailableController(generation),
    preview: latestFact ? factPreview(latestFact) : "",
    tags: importedFromProviderHistory ? ["origin:provider-history"] : [],
    source: { authority, syncState: "current" },
  };
}

export function isTerminalReceipt(status: ReceiptStatusDto): boolean {
  return ["applied", "rejected", "indeterminate", "expired", "cancelled"].includes(
    status,
  );
}

export function projectTerminalReceipt(
  source: ReceiptOutcomeDto,
  operation: CommandReceipt["command"],
): CommandReceipt {
  if (source.status === "applied") return projectReceipt(source, operation);
  const message =
    source.error?.message ||
    `The local Daemon closed ${operation} with ${source.status}.`;
  const code = source.error?.code;
  if (code === "unsupported") throw new OrniTransportError("UNSUPPORTED", message);
  if (code === "not_found") throw new OrniTransportError("NOT_FOUND", message);
  if (code === "forbidden") {
    throw new OrniTransportError("PERMISSION_DENIED", message);
  }
  if (code === "invalid_transition" || code === "revision_conflict") {
    throw new OrniTransportError("CONFLICT", message);
  }
  if (source.status === "expired") {
    throw new OrniTransportError("TIMEOUT", message, true);
  }
  throw new OrniTransportError(
    "UNAVAILABLE",
    message,
    source.error?.retryable ?? false,
  );
}

export function projectReceipt(
  source: ReceiptOutcomeDto,
  operation: CommandReceipt["command"],
): CommandReceipt {
  const status = source.status === "applied"
    ? "succeeded"
    : source.status === "rejected"
      ? "rejected"
      : ["indeterminate", "expired", "cancelled"].includes(source.status)
        ? "failed"
        : source.status === "accepted"
          ? "accepted"
          : "running";
  return {
    id: source.receipt_id || source.command_id,
    command: operation,
    status,
    requestedAt: source.recorded_at,
    completedAt: isTerminalReceipt(source.status) ? source.recorded_at : null,
    detail:
      source.error?.message ??
      (isTerminalReceipt(source.status)
        ? "Command applied by the local Daemon."
        : "Command accepted by the local Daemon."),
  };
}

function projectAutoContinue(source: AutoContinuePolicyDto): AutoContinuePolicy {
  return {
    enabled: source.enabled,
    recoveryEnabled: source.recovery_enabled,
    retryWindowSeconds: source.retry_window_seconds,
    retryForever: source.retry_forever,
    retryIntervalSeconds: source.retry_interval_seconds,
    attemptsUsed: source.attempts_used,
    recoveryStartedAt: source.recovery_started_at ?? null,
    lastRecoveryError: source.last_recovery_error ?? null,
    recoveryHistory: source.recovery_history.map((record) => ({
      cause: record.cause,
      error: record.error,
      attemptedAt: record.attempted_at,
      attemptNumber: record.attempt_number,
      outcome: record.outcome,
      detail: record.detail ?? null,
      completedAt: record.completed_at ?? null,
    })),
  };
}

function projectStatus(source: SessionDto, attempt?: ProcessAttemptDto): SessionStatus {
  if (source.desired_state === "deleted") return "archived";
  const states: Record<ProcessStateDto, SessionStatus> = {
    planned: "starting",
    spawning: "starting",
    running: "running",
    stopping: "stopping",
    exited: "exited",
    failed_to_start: "error",
    lost: "error",
  };
  return attempt ? states[attempt.process_state] : "stopped";
}

function unavailableController(generation: number): ControllerState {
  return {
    mode: "unavailable",
    holderLabel: null,
    leaseId: null,
    expiresAt: null,
    generation,
  };
}

function factPreview(fact: ProviderFactDto): string {
  const keys = ["text", "message", "command", "input", "output", "status"];
  return keys.map((key) => fact.attributes[key]?.trim()).find(Boolean) ?? "";
}

function workspaceName(path: string): string {
  const segments = path.split(/[\\/]/).filter(Boolean);
  return segments.at(-1) ?? path;
}
